import { Loading } from '@/components/shared';
import { useCancelDirectScheduleAdjustmentMutation, useDirectScheduleAdjustmentQuery } from '@/hooks/apis';
import { FlashContext } from '@/providers';
import { Order } from '@/types';
import { bem } from '@/utilities';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { OrderCancelModal } from './orderCancelModal';
import { OrdersList } from './ordersList';

const today = new Date();

export type OrderStatus = 'unassigned' | 'scheduled' | 'finished';

type StatusBarProps = {
  unassignedOrdersCount: number;
  scheduledOrdersCount: number;
  status: OrderStatus;
  token: string;
};

const StatusBar: React.FC<StatusBarProps> = (props) => {
  const { unassignedOrdersCount, scheduledOrdersCount, status, token } = props;
  const navigate = useNavigate();

  return (
    <div className={bem('orders', 'status-bar')}>
      <p
        className={bem('orders', 'status-bar-text', { selected: status === 'unassigned' })}
        onClick={() => navigate(`/orders/${token}?status=unassigned`)}
      >
        {`予約待ち（${unassignedOrdersCount}）`}
      </p>
      <p
        className={bem('orders', 'status-bar-text', { selected: status === 'scheduled', 'sp-only': true })}
        onClick={() => navigate(`/orders/${token}?status=scheduled`)}
      >
        予約済みの整備
        <br className={bem('orders', 'status-bar-text-br')} />
        {`（${scheduledOrdersCount}）`}
      </p>
      <p
        className={bem('orders', 'status-bar-text', { selected: status === 'finished' })}
        onClick={() => navigate(`/orders/${token}?status=finished`)}
      >
        作業済み履歴
      </p>
    </div>
  );
};

type FilterFormViewProps = {
  status: OrderStatus;
  selectedMonthKey: string;
  orders: Order[];
  token: string;
  onChangeSearchWord: (searchWord: string) => void;
};

type OrderMonthCount = {
  [key: string]: number;
};

const FilterFormView: React.FC<FilterFormViewProps> = (props) => {
  const { status, selectedMonthKey, orders, token, onChangeSearchWord } = props;
  const navigate = useNavigate();
  const thisMonthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;
  const [openOptions, setOpenOptions] = useState<boolean>(false);

  const orderMonthsCount: OrderMonthCount = orders.reduce((previousValue: OrderMonthCount, currentOrder: Order) => {
    const scheduledAtDate = new Date(currentOrder.scheduledAt);
    const monthKey = `${scheduledAtDate.getFullYear()}-${scheduledAtDate.getMonth() + 1}`;
    return { ...previousValue, [monthKey]: (previousValue[monthKey] || 0) + 1 };
  }, {});
  const sortedOrderMonthCountKeys = Object.keys(orderMonthsCount).sort((a, b) => {
    const result = new Date(a).getTime() - new Date(b).getTime();
    return status === 'finished' ? -result : result;
  });

  const optionText = (key: string) => {
    if (key === 'all') {
      return `すべて（${orders.length}）`;
    }
    const [year, month] = key.split('-');
    return `${year}年${month}月（${orderMonthsCount[key] || 0}）`;
  };

  const handleClickOption = (key: string) => {
    setOpenOptions(false);
    navigate(`/orders/${token}?status=${status}&month=${key}`);
  };

  return (
    <div className={bem('orders', 'filter-form')}>
      {status !== 'unassigned' && (
        <div className={bem('orders', 'filter-form-select')}>
          <p className={bem('orders', 'filter-form-select__text')} onClick={() => setOpenOptions(!openOptions)}>
            {optionText(selectedMonthKey)}
          </p>
          <div className={bem('orders', 'filter-form-select__options', { open: openOptions })}>
            {status === 'scheduled' && (
              <p className={bem('orders', 'filter-form-select__option')} onClick={() => handleClickOption('all')}>
                {optionText('all')}
              </p>
            )}
            {!sortedOrderMonthCountKeys.includes(thisMonthKey) && (
              <p
                className={bem('orders', 'filter-form-select__option')}
                onClick={() => handleClickOption(thisMonthKey)}
              >
                {optionText(thisMonthKey)}
              </p>
            )}
            {sortedOrderMonthCountKeys.map((key) => {
              return (
                <p
                  className={bem('orders', 'filter-form-select__option')}
                  onClick={() => handleClickOption(key)}
                  key={key}
                >
                  {optionText(key)}
                </p>
              );
            })}
          </div>
        </div>
      )}
      <div className={bem('orders', 'filter-form-search')}>
        <input
          className={bem('orders', 'filter-form-search__input')}
          placeholder='車両ナンバーで検索'
          onChange={(e) => onChangeSearchWord(e.target.value)}
        />
      </div>
    </div>
  );
};

export const OrdersIndex: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const status = (query.get('status') as OrderStatus) || 'unassigned';
  const selectMonth = query.get('month') || `${today.getFullYear()}-${today.getMonth() + 1}`;
  const { token } = useParams();
  const directScheduleAdjustmentQuery = useDirectScheduleAdjustmentQuery(token);
  const updateDirectScheduleAdjustment = useCancelDirectScheduleAdjustmentMutation(token);
  const { setFlash } = useContext(FlashContext);
  const [openOrderCancelModal, setOpenOrderCancelModal] = useState<boolean>(false);
  const [selectCancelOrder, setSelectCancelOrder] = useState<Order>(null);

  const unassignedOrders = directScheduleAdjustmentQuery.data?.orders?.filter((order) => !order.scheduledAt) || [];
  const scheduledOrders =
    directScheduleAdjustmentQuery.data?.orders?.filter(
      (order) => order.scheduledAt && new Date(order.scheduledAt) >= today
    ) || [];
  const finishedOrders =
    directScheduleAdjustmentQuery.data?.orders?.filter(
      (order) => order.scheduledAt && new Date(order.scheduledAt) < today
    ) || [];
  const filteredOrdersByStatus =
    status === 'unassigned' ? unassignedOrders : status === 'scheduled' ? scheduledOrders : finishedOrders;
  const filteredOrdersByMonth = filteredOrdersByStatus.filter((order) => {
    if (!order.scheduledAt || selectMonth === 'all') return true;
    const dateScheduledAt = new Date(order.scheduledAt);
    return selectMonth === `${dateScheduledAt.getFullYear()}-${dateScheduledAt.getMonth() + 1}`;
  });
  const [filteredOrdersBySearchWord, setFilteredOrdersBySearchWord] = useState<Order[]>(filteredOrdersByMonth);
  const [searchWord, setSearchWord] = useState<string>('');

  useEffect(() => {
    setFilteredOrdersBySearchWord(
      filteredOrdersByMonth.filter((order) => order.ownedCar.carNumber.includes(searchWord))
    );
  }, [status, selectMonth, searchWord, directScheduleAdjustmentQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!directScheduleAdjustmentQuery.isFetched) {
    return <Loading />;
  }

  const handleClickSubmitButton = (orderId: number) => {
    updateDirectScheduleAdjustment.mutate(
      { orderId: orderId },
      {
        onSuccess: () => {
          setFlash({
            type: 'success',
            message: <>予約の取り消しが完了しました。</>
          });
        },
        onError: () => {
          setFlash({
            type: 'error',
            message: <>予約の取り消しが失敗しました。</>
          });
        },
        onSettled: () => {
          setSelectCancelOrder(null);
          setOpenOrderCancelModal(false);
          window.scrollTo({ top: 0 });
        }
      }
    );
  };

  return (
    <div className='orders'>
      <div className={bem('orders', 'container')}>
        <StatusBar
          unassignedOrdersCount={unassignedOrders.length}
          scheduledOrdersCount={scheduledOrders.length}
          status={status}
          token={token}
        />
        <FilterFormView
          status={status}
          selectedMonthKey={selectMonth}
          orders={filteredOrdersByStatus}
          token={token}
          onChangeSearchWord={(searchWord) => setSearchWord(searchWord)}
        />
        <OrdersList
          status={status}
          orders={filteredOrdersBySearchWord}
          token={token}
          onClickCancelLink={(order) => {
            setSelectCancelOrder(order);
            setOpenOrderCancelModal(true);
          }}
        />
        <OrderCancelModal
          openModal={openOrderCancelModal}
          order={selectCancelOrder}
          onClickCloseModal={() => {
            setOpenOrderCancelModal(false);
            setSelectCancelOrder(null);
          }}
          onClickSubmitButton={(orderId) => handleClickSubmitButton(orderId)}
        />
      </div>
    </div>
  );
};
